import React, { useEffect, useState } from 'react';
import {
  CalendarClock,
  Check,
  Copy,
  Download,
  FileSpreadsheet,
  Link2,
  Link2Off,
  RefreshCcw,
  RefreshCw,
  Shield
} from 'lucide-react';
import { ErrorState, InlineNotice, LoadingState } from './AsyncState.jsx';
import {
  PRICE_EXPORT_FORMAT_OPTIONS,
  PRICE_EXPORT_INTERVAL_OPTIONS,
  formatExportMoment,
  formatFileSize,
  priceExportIntervalOption,
  priceExportUrl
} from '../priceExport.js';
import './price-export.css';

const API = '/api';
const NUMBER_FORMATTER = new Intl.NumberFormat('ru-RU');

async function api(path, options = {}) {
  const response = await fetch(`${API}${path}`, {
    credentials: 'include',
    method: options.method || 'GET',
    headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
    body: options.body ? JSON.stringify(options.body) : undefined
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Не удалось выполнить запрос');
  return data;
}

function formatCount(value) {
  return NUMBER_FORMATTER.format(Number(value || 0));
}

function downloadsLabel(count) {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${formatCount(count)} раз`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${formatCount(count)} раза`;
  return `${formatCount(count)} раз`;
}

function PriceExportIntro({ columns, busy, onCreate }) {
  return (
    <div className="price-export-intro">
      <span className="price-export-intro-icon" aria-hidden="true">
        <FileSpreadsheet size={28} />
      </span>
      <div className="price-export-intro-copy">
        <h2>Подключите прайс к своей учётной системе</h2>
        <p>
          Создайте персональную ссылку — по ней всегда лежит актуальный прайс с вашими ценами и остатками.
          Настройте загрузку в 1С, Excel или другой программе один раз, дальше файл обновляется сам.
        </p>
        <ul className="price-export-benefits">
          <li>
            <FileSpreadsheet size={16} aria-hidden="true" />
            Пять форматов: CSV, CSV для старой 1С, Excel, XML (YML) и JSON
          </li>
          <li>
            <CalendarClock size={16} aria-hidden="true" />
            Обновление по расписанию: каждый час, день, неделю или месяц
          </li>
          <li>
            <Shield size={16} aria-hidden="true" />
            Цены в файле — те же, что вы видите в каталоге
          </li>
        </ul>
        <div className="price-export-columns" aria-label="Колонки файла">
          {columns.map((column) => (
            <span key={column}>{column}</span>
          ))}
        </div>
        <button type="button" className="button primary price-export-create" onClick={onCreate} disabled={busy}>
          {busy ? <RefreshCw className="spin" size={18} aria-hidden="true" /> : <Link2 size={18} aria-hidden="true" />}
          <span>{busy ? 'Готовим прайс…' : 'Создать ссылку'}</span>
        </button>
      </div>
    </div>
  );
}

function PriceExportFormatRow({ format }) {
  const option = PRICE_EXPORT_FORMAT_OPTIONS[format.key] || {
    badge: format.key.toUpperCase(),
    title: format.key,
    description: ''
  };
  const url = priceExportUrl(format.path, window.location.origin);
  const size = formatFileSize(format.size);
  const [copyState, setCopyState] = useState('idle');

  useEffect(() => {
    if (copyState === 'idle') return undefined;
    const timeoutId = window.setTimeout(() => setCopyState('idle'), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [copyState]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
    } catch {
      setCopyState('manual');
    }
  }

  return (
    <li className="price-export-format">
      <span className={`price-export-format-badge is-${format.key}`} aria-hidden="true">
        {option.badge}
      </span>
      <div className="price-export-format-copy">
        <b>
          {option.title}
          {size ? <small>{size}</small> : null}
        </b>
        <span>{option.description}</span>
      </div>
      <div className="price-export-url-row">
        <code className="price-export-url">{url}</code>
        {copyState === 'manual' ? (
          <small className="price-export-copy-hint">Выделите ссылку и скопируйте её вручную.</small>
        ) : null}
      </div>
      <div className="price-export-format-actions">
        <button
          type="button"
          className={`price-export-copy ${copyState === 'copied' ? 'is-copied' : ''}`.trim()}
          onClick={copy}
          aria-label={`Скопировать ссылку: ${option.title}`}
        >
          {copyState === 'copied' ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
          <span>{copyState === 'copied' ? 'Скопировано' : 'Копировать'}</span>
        </button>
        <a
          className="price-export-download"
          href={url}
          download
          aria-label={`Скачать файл: ${option.title}`}
          title="Скачать файл"
        >
          <Download size={16} aria-hidden="true" />
        </a>
      </div>
    </li>
  );
}

function PriceExportPreview({ columns, rows }) {
  if (!rows.length) return null;
  return (
    <div className="price-export-preview" role="table" aria-label="Первые строки прайса">
      <div className="price-export-preview-row is-head" role="row">
        {columns.map((column) => (
          <span key={column} role="columnheader">
            {column}
          </span>
        ))}
      </div>
      {rows.map((row) => (
        <div className="price-export-preview-row" role="row" key={row.vendorCode}>
          <span role="cell" className="price-export-preview-code">
            {row.vendorCode}
          </span>
          <span role="cell">{row.name}</span>
          <span role="cell" className="price-export-preview-number">
            {formatCount(row.price)}
          </span>
          <span role="cell" className="price-export-preview-number">
            {formatCount(row.stock)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function PriceExportPanel() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [loadError, setLoadError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState({ tone: '', text: '', placement: 'top' });
  const [confirmAction, setConfirmAction] = useState('');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setLoadError('');
    api('/account/price-export')
      .then((nextData) => {
        if (cancelled) return;
        setData(nextData);
        setStatus('success');
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(error.message);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  // Уведомление показываем рядом с кнопкой, которую нажали: панель длинная.
  async function run(action, placement, request, successText) {
    setBusy(action);
    setNotice({ tone: '', text: '', placement });
    try {
      const nextData = await request();
      setData(nextData);
      setConfirmAction('');
      setNotice({ tone: 'success', text: successText, placement: nextData.export ? placement : 'top' });
    } catch (error) {
      setNotice({ tone: 'error', text: error.message, placement });
    } finally {
      setBusy('');
    }
  }

  function noticeAt(placement) {
    if (notice.placement !== placement || !notice.text) return null;
    return (
      <InlineNotice tone={notice.tone || 'info'} className="price-export-notice">
        {notice.text}
      </InlineNotice>
    );
  }

  const exportLink = data?.export || null;
  const columns = data?.columns || ['Артикул', 'Наименование', 'Цена', 'Остаток'];
  const intervalOption = priceExportIntervalOption(exportLink?.interval);

  function changeInterval(interval) {
    if (!exportLink || interval === exportLink.interval || busy) return;
    const option = priceExportIntervalOption(interval);
    run(
      `interval:${interval}`,
      'interval',
      () => api('/account/price-export', { method: 'PATCH', body: { interval } }),
      `Готово: ${option.label.toLowerCase()}. Данные в файле уже обновлены.`
    );
  }

  return (
    <section className="table-panel price-export-panel" aria-labelledby="price-export-title">
      <div className="panel-title price-export-title">
        <div>
          <h1 id="price-export-title">Экспорт</h1>
          <span>Прайс-лист с вашими ценами и остатками по постоянной ссылке</span>
        </div>
        {exportLink ? (
          <span className="price-export-status">
            <i aria-hidden="true" />
            Ссылка активна
          </span>
        ) : null}
      </div>

      {noticeAt('top')}
      {status === 'loading' ? <LoadingState variant="panel" count={3} label="Загружаем настройки экспорта…" /> : null}
      {status === 'error' ? (
        <ErrorState
          title="Не удалось загрузить экспорт"
          message={loadError}
          onRetry={() => setRetryKey((value) => value + 1)}
        />
      ) : null}

      {status === 'success' && !exportLink ? (
        <PriceExportIntro
          columns={columns}
          busy={busy === 'create'}
          onCreate={() =>
            run(
              'create',
              'top',
              () => api('/account/price-export', { method: 'POST', body: { interval: 'hour' } }),
              'Ссылка готова. Скопируйте её в нужном формате и укажите в своей программе.'
            )
          }
        />
      ) : null}

      {status === 'success' && exportLink ? (
        <div className="price-export-body">
          <section className="price-export-block" aria-labelledby="price-export-interval-title">
            <h2 id="price-export-interval-title">Частота обновления</h2>
            <div className="price-export-intervals" role="group" aria-label="Частота обновления файла">
              {PRICE_EXPORT_INTERVAL_OPTIONS.map((option) => {
                const active = option.value === exportLink.interval;
                const pending = busy === `interval:${option.value}`;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={active ? 'active' : ''}
                    aria-pressed={active}
                    disabled={Boolean(busy) && !pending}
                    onClick={() => changeInterval(option.value)}
                  >
                    {pending ? <RefreshCw className="spin" size={15} aria-hidden="true" /> : null}
                    {option.label}
                  </button>
                );
              })}
            </div>
            <p className="price-export-hint">{intervalOption.hint}</p>
            {noticeAt('interval')}
            <div className="price-export-stats">
              <article>
                <small>Данные обновлены</small>
                <b>{formatExportMoment(exportLink.refreshedAt)}</b>
              </article>
              <article>
                <small>Следующее обновление</small>
                <b>{formatExportMoment(exportLink.nextRefreshAt)}</b>
              </article>
              <article>
                <small>Позиций в прайсе</small>
                <b>{exportLink.productCount == null ? '—' : formatCount(exportLink.productCount)}</b>
              </article>
              <article>
                <small>Последнее скачивание</small>
                <b>{exportLink.lastDownloadAt ? formatExportMoment(exportLink.lastDownloadAt) : 'Ещё не скачивали'}</b>
                {exportLink.downloadCount ? <em>всего {downloadsLabel(exportLink.downloadCount)}</em> : null}
              </article>
            </div>
          </section>

          <section className="price-export-block" aria-labelledby="price-export-links-title">
            <h2 id="price-export-links-title">Ссылки на прайс</h2>
            <p className="price-export-hint">
              Все ссылки ведут на один и тот же прайс — выберите формат, который понимает ваша программа.
            </p>
            <ul className="price-export-formats">
              {exportLink.formats.map((format) => (
                <PriceExportFormatRow key={format.key} format={format} />
              ))}
            </ul>
          </section>

          <section className="price-export-block" aria-labelledby="price-export-preview-title">
            <h2 id="price-export-preview-title">Что в файле</h2>
            <PriceExportPreview columns={columns} rows={exportLink.preview || []} />
            <p className="price-export-hint">
              Цены — те же, что вы видите в каталоге. Остаток — сколько доступно к заказу на момент обновления.
              Товары, которых сейчас нет, остаются в файле с остатком 0, чтобы учётная система обнулила их у себя.
            </p>
          </section>

          <footer className="price-export-footer">
            <p>
              <Shield size={18} aria-hidden="true" />
              <span>
                Ссылка открывается без пароля — передавайте её только своей учётной системе и сотрудникам. Если она
                попала к посторонним, перевыпустите её: старая сразу перестанет работать.
              </span>
            </p>
            {confirmAction ? (
              <div className="price-export-confirm" role="alert">
                <p>
                  {confirmAction === 'regenerate'
                    ? 'Старые ссылки перестанут работать. Новую нужно будет заново указать в учётной системе.'
                    : 'Все ссылки перестанут работать. Включить экспорт можно будет снова, но уже с новой ссылкой.'}
                </p>
                <div>
                  <button type="button" onClick={() => setConfirmAction('')} disabled={Boolean(busy)}>
                    Отмена
                  </button>
                  {confirmAction === 'regenerate' ? (
                    <button
                      type="button"
                      className="button primary"
                      disabled={Boolean(busy)}
                      onClick={() =>
                        run(
                          'regenerate',
                          'footer',
                          () => api('/account/price-export/regenerate', { method: 'POST' }),
                          'Ссылка перевыпущена. Обновите её в своей учётной системе.'
                        )
                      }
                    >
                      {busy === 'regenerate' ? <RefreshCw className="spin" size={16} aria-hidden="true" /> : <RefreshCcw size={16} aria-hidden="true" />}
                      <span>Перевыпустить</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="button price-export-danger"
                      disabled={Boolean(busy)}
                      onClick={() =>
                        run(
                          'disable',
                          'footer',
                          () => api('/account/price-export', { method: 'DELETE' }),
                          'Экспорт отключён, ссылки больше не работают.'
                        )
                      }
                    >
                      {busy === 'disable' ? <RefreshCw className="spin" size={16} aria-hidden="true" /> : <Link2Off size={16} aria-hidden="true" />}
                      <span>Отключить</span>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="price-export-footer-actions">
                <button type="button" className="button" onClick={() => setConfirmAction('regenerate')} disabled={Boolean(busy)}>
                  <RefreshCcw size={16} aria-hidden="true" />
                  <span>Перевыпустить ссылку</span>
                </button>
                <button
                  type="button"
                  className="button price-export-danger-ghost"
                  onClick={() => setConfirmAction('disable')}
                  disabled={Boolean(busy)}
                >
                  <Link2Off size={16} aria-hidden="true" />
                  <span>Отключить экспорт</span>
                </button>
              </div>
            )}
            {noticeAt('footer')}
          </footer>
        </div>
      ) : null}
    </section>
  );
}
